import React, { useEffect } from "react";
import { Select, SelectProps } from "@mantine/core";
import { AppIcon } from "@/components/generic/AppIcon";

const MESSAGE_SOURCE_OPTIONS = [
  {
    value: "local",
    label: "Local",
    icon: "Check",
    color: "green",
    description: "Display entered message",
    disabled: false,
  },
  {
    value: "http",
    label: "HTTP",
    icon: "Check",
    color: "black",
    description: "Receive message from provided URL",
    disabled: true,
  },
  {
    value: "rss",
    label: "RSS",
    icon: "Check",
    color: "black",
    description: "Receive messages from RSS feed",
    disabled: true,
  },
] as const;

export interface MessageSourceSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Current status value (100 for enabled, 0 for disabled) */
  value: string | null;

  required?: boolean;

  /** Callback when status changes */
  onChange: (value: string | null) => void;

  /** Whether to show icons in the dropdown */
  withIcons?: boolean;

  /** Whether to show descriptions in the dropdown */
  withDescriptions?: boolean;
}

export const MessageSourceSelector: React.FC<MessageSourceSelectorProps> = ({
  value,
  onChange,
  required,
  withIcons = true,
  withDescriptions = false,
  placeholder = "Select source",
  ...selectProps
}) => {
  // Convert numeric value to string for Select component
  const stringValue = value !== null ? String(value) : null;

  // Prepare select data with optional descriptions
  const selectData = MESSAGE_SOURCE_OPTIONS.map((option) => {
    let label: string = option.label;

    if (withDescriptions) {
      label = `${option.label} - ${option.description}`;
    }

    return {
      value: option.value,
      label,
      disabled: option.disabled,
    };
  });

  // Auto-select first display type if required and no value is set
  useEffect(() => {
    if (required && !value && onChange) {
      const firstOption = MESSAGE_SOURCE_OPTIONS[0];
      if (firstOption && !firstOption.disabled) {
        onChange(firstOption.value);
      }
    }
  }, [required, value, onChange]);

  // Handle change and convert back to number
  const handleChange = (stringValue: string | null) => {
    if (stringValue === null) {
      onChange(null);
    } else {
      onChange(stringValue);
    }
  };

  // Get current option for icon display
  const currentOption = MESSAGE_SOURCE_OPTIONS.find(
    (opt) => opt.value === stringValue
  );
  const leftSection =
    withIcons && currentOption ? (
      <AppIcon
        iconName={currentOption.icon as any}
        size={16}
        style={{
          color:
            currentOption.color === "green"
              ? "var(--mantine-color-green-6)"
              : "var(--mantine-color-red-6)",
        }}
      />
    ) : undefined;

  return (
    <Select
      {...selectProps}
      data={selectData}
      value={stringValue}
      onChange={handleChange}
      placeholder={placeholder}
      leftSection={leftSection}
      clearable={false}
    />
  );
};

// Export status options for use in other components
export { MESSAGE_SOURCE_OPTIONS };
